import dayjs from 'dayjs';
import { Feature, Geometry } from 'geojson';
import { usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ButtonVariant } from '@/components/button';
import Chip from '@/components/chip/chipV2';
import Typography from '@/components/typography';
import { PLAT_MAP_URL } from '@/constants/urls';
import {
  ConsolidatedCompSources,
  LiteConsolidatedComp,
} from '@/endpoints/itasca/comps/queryConsolidatedComps';
import { CardCloseButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardCloseButton';
import { CardFooterButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardFooterButton';
import {
  confidentialColor,
  contributoryColor,
  courthouseColor,
  curatedColor,
} from '@/mapbox-layers/comps';
import { AcreTraderFeature, Comp, OwnertransferFeature } from '@/models/comps';
import { IAcresCompFeature } from '@/models/features';
import { CompSourcesEnumsBE } from '@/models/features/comp';
import { convertMoney } from '@/utils/common';
import {
  getCompFeatureAcres,
  getCompFeatureDate,
  getCompFeaturePrice,
  getCompPricePerAcre,
} from '@/utils/comps';

import { useGenericSelectionPanelFunctions } from '../../hooks/useGenericSelectionPanelFunctions';
import { formatAcres } from '../../util/determineSelectionsPanelAcres';
import { Card } from '../baseCard/card';
import { LineItem } from '../baseCard/lineItem';

type Props = {
  feature: IAcresCompFeature;
  idx: number;
};

export const getColorFromCompFeature = (
  compFeature: OwnertransferFeature | AcreTraderFeature | IAcresCompFeature
): { backgroundColor: string; textColor: 'light' | 'dark' } => {
  // Is an internal comp
  if ((compFeature as AcreTraderFeature).properties.comp_type) {
    const type = (compFeature as IAcresCompFeature).properties.visibility;
    switch (type) {
      case CompSourcesEnumsBE.CONTRIBUTORY:
        return { backgroundColor: contributoryColor, textColor: 'dark' };
      case CompSourcesEnumsBE.CURATED:
        return { backgroundColor: curatedColor, textColor: 'light' };
      case CompSourcesEnumsBE.PRIVATE:
        return { backgroundColor: confidentialColor, textColor: 'light' };
      case CompSourcesEnumsBE.COURTHOUSE:
        return { backgroundColor: courthouseColor, textColor: 'dark' };
      default:
        break;
    }
  }

  // Is a courthouse comp
  return { backgroundColor: courthouseColor, textColor: 'dark' };
};

export const getDisplayNumberColors = (
  compFeature: OwnertransferFeature | AcreTraderFeature | IAcresCompFeature
): { background: string; text: string } => {
  const darkStyle = { background: 'bg-gray-100', text: 'text-white' };
  const lightStyle = { background: 'bg-white', text: 'text-gray-100' };

  // Is an internal comp
  if ((compFeature as AcreTraderFeature).properties.comp_type) {
    const type = (compFeature as IAcresCompFeature).properties.visibility;
    switch (type) {
      case CompSourcesEnumsBE.CONTRIBUTORY:
        return darkStyle;
      case CompSourcesEnumsBE.CURATED:
        return lightStyle;
      case CompSourcesEnumsBE.PRIVATE:
        return lightStyle;
      case CompSourcesEnumsBE.COURTHOUSE:
        return darkStyle;
      default:
        break;
    }
  }

  // Is a courthouse comp
  return darkStyle;
};

const getColorFromConsolidatedCompFeature = (
  compFeature: Feature<Geometry, LiteConsolidatedComp>
): { backgroundColor: string; textColor: 'light' | 'dark' } => {
  // Is an internal comp
  if (compFeature.properties.sale.visibility) {
    const type = compFeature.properties.sale.visibility;
    switch (type) {
      case ConsolidatedCompSources.CONTRIBUTED:
        return { backgroundColor: contributoryColor, textColor: 'dark' };
      case ConsolidatedCompSources.CURATED:
        return { backgroundColor: curatedColor, textColor: 'light' };
      case ConsolidatedCompSources.PRIVATE:
        return { backgroundColor: confidentialColor, textColor: 'light' };
      case ConsolidatedCompSources.COURTHOUSE:
        return { backgroundColor: courthouseColor, textColor: 'dark' };
      default:
        break;
    }
  }

  // Is a courthouse comp
  return { backgroundColor: courthouseColor, textColor: 'dark' };
};

export const getDisplayNumberColorsConsolidatedComp = (
  compFeature: Feature<Geometry, LiteConsolidatedComp>
): { background: string; text: string } => {
  const darkStyle = { background: 'bg-gray-100', text: 'text-white' };
  const lightStyle = { background: 'bg-white', text: 'text-gray-100' };

  // Is an internal comp
  if (compFeature.properties.sale.visibility) {
    const type = compFeature.properties.sale.visibility;
    switch (type) {
      case ConsolidatedCompSources.CONTRIBUTED:
        return darkStyle;
      case ConsolidatedCompSources.CURATED:
        return lightStyle;
      case ConsolidatedCompSources.PRIVATE:
        return lightStyle;
      case ConsolidatedCompSources.COURTHOUSE:
        return darkStyle;
      default:
        break;
    }
  }

  // Is a courthouse comp
  return darkStyle;
};

const EMPTY = '-';

export const SoldLandCard: FC<Props> = ({ feature, idx }) => {
  const router = useRouter();
  const pathname = usePathname();

  const [isHovered, setIsHovered] = useState(false);
  const {
    removeFeature,
    openCompDetails,
    handleMouseOff,
    handleMouseOn,
    goToGeometryLocation,
  } = useGenericSelectionPanelFunctions();

  /**
   * This component needs to handle both consolidated comps and non consolidated comsp (courthouse/user comps)
   * since there are a variety of locations that can populate the features for comps (sigh)
   */
  const isConsolidatedCompFeature = useMemo(() => {
    return 'sale' in feature.properties;
  }, [feature]);

  /** The properties of the consolidated comp, null if not a consolidated comp */
  const consolidatedProperties = useMemo(() => {
    if (isConsolidatedCompFeature) {
      return feature.properties as any as LiteConsolidatedComp;
    }
    return null;
  }, [isConsolidatedCompFeature, feature.properties]);

  const { properties } = feature;
  const acres = useMemo(() => {
    if (isConsolidatedCompFeature) {
      return formatAcres(consolidatedProperties?.sale?.acres ?? 0, false);
    }

    return formatAcres(getCompFeatureAcres(feature), false);
  }, [consolidatedProperties?.sale?.acres, feature, isConsolidatedCompFeature]);

  const totalPrice = useMemo(() => {
    if (isConsolidatedCompFeature) {
      return convertMoney(consolidatedProperties?.sale?.sale_amount ?? 0);
    }

    return convertMoney(getCompFeaturePrice(feature));
  }, [
    isConsolidatedCompFeature,
    feature,
    consolidatedProperties?.sale?.sale_amount,
  ]);
  const pricePerAcre = useMemo(() => {
    // Consolidated
    if (isConsolidatedCompFeature) {
      const ppa = consolidatedProperties?.sale.price_per_acre;
      return ppa ? convertMoney(ppa) : '$0';
    }

    const ppa = getCompPricePerAcre(properties as unknown as Comp);
    return ppa ? convertMoney(ppa) : '$0';
  }, [
    isConsolidatedCompFeature,
    properties,
    consolidatedProperties?.sale.price_per_acre,
  ]);

  const date = useMemo(() => {
    if (isConsolidatedCompFeature) {
      const dte = consolidatedProperties?.sale.formatted_sale_date;
      return dte ? dayjs(dte).format('MM-DD-YYYY') : EMPTY;
    }

    const newDate = getCompFeatureDate(feature);
    return newDate ? dayjs(newDate).format('MM-DD-YYYY') : EMPTY;
  }, [
    isConsolidatedCompFeature,
    feature,
    consolidatedProperties?.sale.formatted_sale_date,
  ]);

  useEffect(() => {
    if (isHovered) {
      handleMouseOn(feature);
    } else {
      handleMouseOff(feature);
    }
  }, [feature, handleMouseOff, handleMouseOn, isHovered]);

  const isAlreadyOnCompDetailsPage = useMemo(() => {
    if (!feature || !pathname) return false;

    return (
      pathname.includes(String(feature.properties?.source_id)) ||
      pathname.includes(String(feature.properties?.id)) ||
      pathname.includes(String(feature.id))
    );
  }, [pathname, feature]);

  const handleOnClose = useCallback(() => {
    removeFeature(feature);
  }, [feature, removeFeature]);

  const viewCompDetails = useCallback(() => {
    openCompDetails(feature);
  }, [feature, openCompDetails]);

  const hideCompDetails = useCallback(() => {
    router.push(PLAT_MAP_URL);
  }, [router]);

  const compChipColor = useMemo(() => {
    if (isConsolidatedCompFeature) {
      return getColorFromConsolidatedCompFeature(feature as any);
    }

    return getColorFromCompFeature(feature);
  }, [feature, isConsolidatedCompFeature]);

  const displayNumberStyle = useMemo(() => {
    if (isConsolidatedCompFeature) {
      return getDisplayNumberColorsConsolidatedComp(feature as any);
    }

    return getDisplayNumberColors(feature);
  }, [feature, isConsolidatedCompFeature]);

  const dateSoldText = useMemo(() => {
    if (feature?.properties?.comp_type === 'auction') {
      return 'Auctioned';
    }
    return 'Date Sold';
  }, [feature]);

  const flyToComp = useCallback(
    (e) => {
      e.stopPropagation();
      goToGeometryLocation(feature);
    },
    [feature, goToGeometryLocation]
  );

  return (
    <Card
      onClick={flyToComp}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <Chip
            canRemove={false}
            color={compChipColor.backgroundColor}
            displayNumber={idx + 1}
            displayNumberColor={displayNumberStyle}
            label={pricePerAcre}
            textColor={compChipColor.textColor}
            variant="light"
          />
          <Typography className="ml-2 leading-5">/acre</Typography>
          <CardCloseButton className="ml-auto" onClick={handleOnClose} />
        </div>
        <div>
          <LineItem label="Total Price" value={totalPrice} />
          <LineItem label="Acres" value={acres} />
          <LineItem label={dateSoldText} value={date} />
        </div>
        {isAlreadyOnCompDetailsPage ? (
          <CardFooterButton
            text="Hide Details"
            variant={ButtonVariant.PRIMARY_LIGHT}
            onClick={hideCompDetails}
          />
        ) : (
          <CardFooterButton text="View Details" onClick={viewCompDetails} />
        )}
      </div>
    </Card>
  );
};
